import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../firebase-config";
import {
  collection,
  doc,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { sendTeamInviteMessage } from "../utils/chatUtils";

export const TeamCreation = ({ clientObj, onTeamCreated }) => {
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState("🎸");
  const [teamMembers, setTeamMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const dropdownRef = useRef(null);

  const avatarOptions = ["🎸", "🎹", "🎤", "🥁", "🎺"];

  // Fetch all non-anonymous users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      const usersRef = collection(db, "users");
      const unsuscribe = onSnapshot(usersRef, (snapshot) => {
        let users = [];
        snapshot.forEach((doc) => {
          const userData = doc.data();
          // Filter out anonymous users and current user
          if (
            userData && 
            !userData.isAnonymous && 
            doc.id !== auth.currentUser?.uid &&
            (userData.displayName || userData.email)
          ) {
            users.push({ 
              ...userData, 
              id: doc.id 
            });
          }
        });
        
        // Sort users alphabetically by displayName or email
        users.sort((a, b) => {
          const aName = (a.displayName || a.email || '').toLowerCase();
          const bName = (b.displayName || b.email || '').toLowerCase();
          return aName.localeCompare(bName);
        });
        
        setAllUsers(users);
      });

      return () => unsuscribe();
    };

    fetchUsers();
  }, []);

  // Filter users based on search input
  useEffect(() => {
    if (memberSearch.trim()) {
      const searchLower = memberSearch.toLowerCase().trim();
      const filtered = allUsers.filter((user) => {
        // Don't show users already in team
        if (teamMembers.some(member => member.id === user.id)) {
          return false;
        }
        
        const displayName = (user.displayName || "").toLowerCase();
        const email = (user.email || "").toLowerCase();
        
        return displayName.includes(searchLower) || email.includes(searchLower);
      });
      setFilteredUsers(filtered);
      setShowUserDropdown(filtered.length > 0);
    } else {
      setFilteredUsers([]);
      setShowUserDropdown(false);
    }
  }, [memberSearch, allUsers, teamMembers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar);
  };

  const handleAddMember = (user) => {
    if (!teamMembers.some(member => member.id === user.id)) {
      // Create member object with only defined values
      const memberData = {
        id: user.id,
        displayName: user.displayName || user.email || "Unknown"
      };
      
      // Only add optional fields if they exist
      if (user.email) memberData.email = user.email;
      if (user.photoURL) memberData.photoURL = user.photoURL;
      
      setTeamMembers([...teamMembers, memberData]);
      setMemberSearch("");
      setShowUserDropdown(false);
      
      // Optional: Send immediate notification if team name is already set
      // This would be for a future feature where members can be added after team creation
      // For now, messages are sent when the team is created
    }
  };

  const handleRemoveMember = (userId) => {
    setTeamMembers(teamMembers.filter(member => member.id !== userId));
  };

  const handleCreateTeam = async () => {
    // Validate inputs
    if (!teamName.trim()) {
      setSaveMessage("Please enter a team name");
      setTimeout(() => setSaveMessage(""), 3000);
      return;
    }

    setIsLoading(true);
    setSaveMessage("");

    try {
      // Fetch the actual gameId from the client's document
      let gameId = "default";
      if (clientObj?.clientId) {
        try {
          const clientDoc = await getDoc(doc(db, "users", clientObj.clientId));
          if (clientDoc.exists()) {
            const clientData = clientDoc.data();
            gameId = clientData.gameId || clientData.game_id || "default";
            console.log("Fetched gameId from client:", gameId);
          } else {
            console.warn("Client document not found, using default gameId");
          }
        } catch (error) {
          console.error("Error fetching client document:", error);
        }
      }

      // Helper function to clean member data (remove undefined values)
      const cleanMemberData = (member, role) => {
        const cleaned = {
          id: member.id || auth.currentUser.uid,
          displayName: member.displayName || member.email || "Unknown",
          role: role
        };
        
        // Only add optional fields if they have values
        if (member.email) cleaned.email = member.email;
        if (member.photoURL) cleaned.photoURL = member.photoURL;
        
        return cleaned;
      };

      // Create team document
      const teamData = {
        teamName: teamName.trim(),
        teamDescription: teamDescription.trim() || "",
        teamAvatar: selectedAvatar,
        gameId: gameId, // Use the fetched gameId from client document
        clientId: clientObj?.clientId || "default", // Keep clientId for reference
        teamMembers: [
          // Add current user as first member with admin role
          cleanMemberData({
            id: auth.currentUser.uid,
            displayName: auth.currentUser.displayName,
            email: auth.currentUser.email,
            photoURL: auth.currentUser.photoURL
          }, "admin"), // Changed from "owner" to "admin"
          // Add invited members
          ...teamMembers.map(member => cleanMemberData(member, "member"))
        ],
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser.uid, // Track who created the team
        isActive: true
      };

      // Save to root teams collection
      const teamsRef = collection(db, "teams");
      const newTeamRef = await addDoc(teamsRef, teamData);

      // Update the games document with the new team
      if (clientObj?.clientId && gameId) {
        try {
          const gamesDocRef = doc(db, "users", clientObj.clientId, "games", gameId);
          const gamesDoc = await getDoc(gamesDocRef);

          // Prepare team data for games document
          // Add ready status to each team member (admin is auto-ready, others need confirmation)
          const teamMembersWithReadyStatus = teamData.teamMembers.map(member => ({
            ...member,
            ready: member.role === "admin" // Admin is automatically ready
          }));
          
          const teamForGames = {
            teamId: newTeamRef.id,
            teamName: teamName.trim(),
            teamAvatar: selectedAvatar,
            teamDescription: teamDescription.trim() || "",
            teamMembers: teamMembersWithReadyStatus,
            createdBy: auth.currentUser.uid,
            createdAt: new Date().toISOString(),
            isActive: true,
            ready: teamMembers.length === 0 // Team is ready if only admin (no other members)
          };

          if (gamesDoc.exists()) {
            // Update existing games document - add team to teams array
            await updateDoc(gamesDocRef, {
              teams: arrayUnion(teamForGames),
              lastUpdated: serverTimestamp()
            });
            console.log("Team added to existing games document");
          } else {
            // Create new games document with this team
            await setDoc(gamesDocRef, {
              gameId: gameId,
              clientId: clientObj.clientId,
              teams: [teamForGames],
              createdAt: serverTimestamp(),
              lastUpdated: serverTimestamp()
            });
            console.log("Created new games document with team");
          }
        } catch (error) {
          console.error("Error updating games document:", error);
          // Don't fail the team creation if games document update fails
        }
      }

      // Send invitation messages to all team members
      const sendInvitePromises = teamMembers.map(async (member) => {
        try {
          await sendTeamInviteMessage(
            member, 
            teamName.trim(), 
            selectedAvatar, 
            newTeamRef.id, 
            gameId, 
            clientObj?.clientId || "default"
          );
          console.log(`Invitation sent to ${member.displayName || member.email}`);
        } catch (error) {
          console.error(`Failed to send invite to ${member.displayName}:`, error);
        }
      });

      // Wait for all invitations to be sent (but don't block on failures)
      await Promise.allSettled(sendInvitePromises);

      setSaveMessage("Team created successfully!");
      // Reset form
      setTeamName("");
      setTeamDescription("");
      setSelectedAvatar("🎸");
      setTeamMembers([]);
      
      // Notify parent component after a short delay
      setTimeout(() => {
        setSaveMessage("");
        if (onTeamCreated) {
          onTeamCreated();
        }
      }, 1500);
    } catch (error) {
      console.error("Error creating team:", error);
      setSaveMessage("Error creating team. Please try again.");
      setTimeout(() => setSaveMessage(""), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setTeamName("");
    setTeamDescription("");
    setSelectedAvatar("🎸");
    setTeamMembers([]);
    setMemberSearch("");
    setSaveMessage("");
  };

  return (
    <div className="team-creation">
      <div className="team-creation-container">
        <div className="team-header">
          <h2 className="text-2xl font-bold text-white mb-4">
            Create Your Team
          </h2>
          <p className="text-gray-300 mb-6">
            Build your dream team and compete together
          </p>
        </div>

        {saveMessage && (
          <div className={`save-message ${saveMessage.includes("Error") ? "error" : "success"}`}>
            {saveMessage}
          </div>
        )}

        <div className="team-form">
          <div className="form-group">
            <label className="text-white mb-2 block">Team Name</label>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter team name"
              className="input-field"
            />
          </div>

          <div className="form-group">
            <label className="text-white mb-2 block">Team Description</label>
            <textarea
              value={teamDescription}
              onChange={(e) => setTeamDescription(e.target.value)}
              placeholder="Describe your team"
              className="input-field textarea"
              rows="4"
            />
          </div>

          <div className="team-avatar-section">
            <label className="text-white mb-2 block">Team Avatar</label>
            <div className="avatar-options">
              {avatarOptions.map((avatar) => (
                <div
                  key={avatar}
                  className={`avatar-option ${selectedAvatar === avatar ? 'selected' : ''}`}
                  onClick={() => handleAvatarSelect(avatar)}
                >
                  <span>{avatar}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="invite-members">
            <h3 className="text-xl font-semibold text-white mb-3">
              Invite Members
            </h3>
            <div className="invite-input-group" ref={dropdownRef}>
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                onFocus={() => memberSearch && setShowUserDropdown(true)}
                placeholder="Enter username or email"
                className="input-field"
              />
              
              {showUserDropdown && (
                <div className="user-dropdown">
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className="user-dropdown-item"
                        onClick={() => handleAddMember(user)}
                      >
                        <div className="user-avatar">
                          {user.photoURL ? (
                            <img src={user.photoURL} alt={user.displayName} />
                          ) : (
                            <span className="avatar-placeholder">
                              {(user.displayName || user.email || "?")[0].toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="user-info">
                          <div className="user-name">
                            {user.displayName || user.email || "Unknown User"}
                          </div>
                          {user.email && user.displayName && (
                            <div className="user-email">{user.email}</div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-users-found">No users found</div>
                  )}
                </div>
              )}
            </div>

            <div className="team-members-list">
              {teamMembers.length > 0 ? (
                <>
                  <p className="text-gray-300 text-sm mb-2">
                    {teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''} added
                  </p>
                  {teamMembers.map((member) => (
                    <div key={member.id} className="team-member">
                      <div className="member-info">
                        <div className="member-avatar">
                          {member.photoURL ? (
                            <img src={member.photoURL} alt={member.displayName} />
                          ) : (
                            <span className="avatar-placeholder">
                              {(member.displayName || member.email || "?")[0].toUpperCase()}
                            </span>
                          )}
                        </div>
                        <span className="member-name">{member.displayName}</span>
                      </div>
                      <button
                        className="remove-member"
                        onClick={() => handleRemoveMember(member.id)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-gray-400 text-sm">No members invited yet</p>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button 
              className="btn-cancel" 
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              className="btn-create" 
              onClick={handleCreateTeam}
              disabled={isLoading}
            >
              {isLoading ? "Creating..." : "Create Team"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};